import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SimpleBannerUpload } from "@/components/community/banner/SimpleBannerUpload";
import { BannerList } from "@/components/community/admin/banner/BannerList";
import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";
import { useNoIndex } from "@/hooks/useCanonicalTag";
import { BANNER_SIZES, type BannerType, type DisplayType } from "@/components/community/banner/bannerSizes";


interface Banner {
    id: string;
    title: string;
    description: string | null;
    image_url: string;
    link_url: string | null;
    is_active: boolean;
    type: BannerType;
    display: DisplayType;
    created_at: string;
    updated_at: string;
}

export function BannersPage() {
  useNoIndex();
    const [banners, setBanners] = useState([]);
    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<"DESKTOP" | "MOBILE">("DESKTOP");
    const [isTabChanging, setIsTabChanging] = useState(false);

    const fetchBanners = async () => {
        setIsLoading(true);
        const { data } = await supabase
            .from("community_banners")
            .select("*")
            .order("created_at", { ascending: false });

        if (data) {
            setBanners(data);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        fetchBanners();
    }, []);

    const handleAddBanner = () => {
        setIsUploadDialogOpen(true);
    };

    const handleTabChange = (value: string) => {
        setIsTabChanging(true);
        setActiveTab(value as "DESKTOP" | "MOBILE");

        setTimeout(() => {
            setIsTabChanging(false);
        }, 300);
    };

    const desktopBanners = banners.filter(banner => banner.display === "DESKTOP");
    const mobileBanners = banners.filter(banner => banner.display === "MOBILE");

    const LoadingSpinner = () => (
        <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
    );

    const BannerSection = ({ title, banners, type }: { title: string, banners: Banner[], type: BannerType }) => {
        const size = BANNER_SIZES[type][activeTab];
        const note = BANNER_SIZES[type].note;
        return (
        <Card>
            <CardHeader className="pb-3 md:pb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <CardTitle className="text-lg md:text-xl">{title}</CardTitle>
                    <span className="text-xs font-medium text-muted-foreground bg-muted px-2.5 py-1 rounded-md">
                        Recomendado ({activeTab === "DESKTOP" ? "Desktop" : "Mobile"}): <strong className="text-foreground">{size}</strong> · {note}
                    </span>
                </div>
            </CardHeader>
            <CardContent className="pt-0">
                {isTabChanging ? (
                    <LoadingSpinner />
                ) : (
                    <BannerList
                        banners={banners.filter((banner) => banner.type === type)}
                        onUpdate={fetchBanners}
                    />
                )}
            </CardContent>
        </Card>
        );
    };


    if (isLoading) {
        return (
            <CandidateLayout maxWidth="6xl">
                <LoadingSpinner />
            </CandidateLayout>
        );
    }

    return (
        <CandidateLayout maxWidth="6xl">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 md:mb-6 gap-3">
                <h1 className="text-xl md:text-2xl font-bold">Gerenciamento de Banners</h1>
                <Button onClick={handleAddBanner} className="w-full sm:w-auto">
                    Adicionar Banner
                </Button>
            </div>

            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4 md:mb-6">
                    <TabsTrigger value="DESKTOP">Desktop</TabsTrigger>
                    <TabsTrigger value="MOBILE">Mobile</TabsTrigger>
                </TabsList>

                <TabsContent value="DESKTOP" className="space-y-4 md:space-y-6">
                    <BannerSection title="Banners de Apresentação" banners={desktopBanners} type="INTRODUCTION" />
                    <BannerSection title="Banners de Aprendizado" banners={desktopBanners} type="LEARNING" />
                    <BannerSection title="Banners de Dúvidas" banners={desktopBanners} type="QUESTIONS" />
                    <BannerSection title="Banners Publicitários" banners={desktopBanners} type="HOME_ADS" />
                    
                    <BannerSection title="Banners de Vagas Públicas (/vagas)" banners={desktopBanners} type="PUBLIC_JOBS" />
                    <BannerSection title="Banners do Feed" banners={desktopBanners} type="FEED" />
                    <BannerSection title="Banners do Comece Aqui" banners={desktopBanners} type="GETTING_STARTED" />
                </TabsContent>

                <TabsContent value="MOBILE" className="space-y-4 md:space-y-6">
                    <BannerSection title="Banners de Apresentação" banners={mobileBanners} type="INTRODUCTION" />
                    <BannerSection title="Banners de Aprendizado" banners={mobileBanners} type="LEARNING" />
                    <BannerSection title="Banners de Dúvidas" banners={mobileBanners} type="QUESTIONS" />
                    <BannerSection title="Banners Publicitários" banners={mobileBanners} type="HOME_ADS" />
                    
                    <BannerSection title="Banners de Vagas Públicas (/vagas)" banners={mobileBanners} type="PUBLIC_JOBS" />
                    <BannerSection title="Banners do Feed" banners={mobileBanners} type="FEED" />
                    <BannerSection title="Banners do Comece Aqui" banners={mobileBanners} type="GETTING_STARTED" />
                </TabsContent>
            </Tabs>

            <SimpleBannerUpload
                open={isUploadDialogOpen}
                onOpenChange={setIsUploadDialogOpen}
                onSuccess={fetchBanners}
            />
        </CandidateLayout>
    );
}
